/**
 * 存储服务 - 使用Dexie.js封装IndexedDB
 * 负责SOS记录和可疑上报的增删改查
 */

import Dexie, { type Table } from 'dexie';
import type { SOSRecord, SuspectedReport, FavoriteChild } from '../types';

// 数据库类
class GuardianDatabase extends Dexie {
  sosRecords!: Table<SOSRecord>;
  suspectedReports!: Table<SuspectedReport>;
  favoriteChildren!: Table<FavoriteChild>;

  constructor() {
    super('GuardianDB');

    this.version(1).stores({
      sosRecords: 'id, reportNumber, syncStatus, timestamp',
      suspectedReports: 'id, reportNumber, caseType, syncStatus, timestamp',
      favoriteChildren: 'id, childId, addedAt'
    });
  }
}

// 创建数据库实例
const db = new GuardianDatabase();

/**
 * 生成UUID
 */
export function generateUUID(): string {
  return crypto.randomUUID();
}

/**
 * 生成求救编号
 * 格式: SOS-YYYYMMDD-XXXXX
 */
export function generateReportNumber(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const randomNum = Math.floor(Math.random() * 99999).toString().padStart(5, '0');
  return `SOS-${dateStr}-${randomNum}`;
}

/**
 * 添加SOS记录
 */
export async function addSOSRecord(record: Omit<SOSRecord, 'id' | 'reportNumber' | 'timestamp' | 'syncStatus' | 'syncAttempts' | 'lastSyncAttempt'>): Promise<SOSRecord> {
  const fullRecord: SOSRecord = {
    ...record,
    id: generateUUID(),
    reportNumber: generateReportNumber(),
    timestamp: Date.now(),
    syncStatus: 'pending',
    syncAttempts: 0,
    lastSyncAttempt: null
  };

  await db.sosRecords.add(fullRecord);
  return fullRecord;
}

/**
 * 获取所有SOS记录（按时间倒序）
 */
export async function getAllSOSRecords(): Promise<SOSRecord[]> {
  return db.sosRecords.orderBy('timestamp').reverse().toArray();
}

/**
 * 获取单个SOS记录
 */
export async function getSOSRecord(id: string): Promise<SOSRecord | undefined> {
  return db.sosRecords.get(id);
}

/**
 * 更新同步状态
 */
export async function updateSyncStatus(
  id: string,
  status: 'pending' | 'synced' | 'failed',
  incrementAttempts: boolean = false
): Promise<void> {
  await db.sosRecords.update(id, {
    syncStatus: status,
    lastSyncAttempt: Date.now(),
    ...(incrementAttempts && {
      syncAttempts: (await db.sosRecords.get(id))!.syncAttempts + 1
    })
  });
}

/**
 * 删除SOS记录
 */
export async function deleteSOSRecord(id: string): Promise<void> {
  await db.sosRecords.delete(id);
}

/**
 * 获取待同步的记录
 */
export async function getPendingRecords(): Promise<SOSRecord[]> {
  return db.sosRecords.where('syncStatus').equals('pending').toArray();
}

/**
 * 获取同步失败的记录
 */
export async function getFailedRecords(): Promise<SOSRecord[]> {
  return db.sosRecords.where('syncStatus').equals('failed').toArray();
}

/**
 * 清除所有本地数据
 */
export async function clearAllData(): Promise<void> {
  await db.sosRecords.clear();
}

/**
 * 清除已同步且超过30天的记录
 */
export async function cleanupOldRecords(): Promise<number> {
  const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

  const oldRecords = await db.sosRecords
    .where('syncStatus')
    .equals('synced')
    .and(record => record.timestamp < thirtyDaysAgo)
    .toArray();

  const idsToDelete = oldRecords.map(record => record.id);
  await db.sosRecords.bulkDelete(idsToDelete);

  return idsToDelete.length;
}

/**
 * 获取记录数量
 */
export async function getRecordCount(): Promise<number> {
  return db.sosRecords.count();
}

/**
 * 检查存储是否接近上限（50条）
 */
export async function isNearStorageLimit(): Promise<boolean> {
  const count = await getRecordCount();
  return count >= 45; // 接近上限时提示
}

// ==================== 可疑上报相关 ====================

/**
 * 生成可疑上报编号
 * 格式: RPT-YYYYMMDD-XXXXX
 */
export function generateReportNumberForSuspected(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const randomNum = Math.floor(Math.random() * 99999).toString().padStart(5, '0');
  return `RPT-${dateStr}-${randomNum}`;
}

/**
 * 添加可疑上报记录
 */
export async function addSuspectedReport(
  record: Omit<SuspectedReport, 'id' | 'reportNumber' | 'timestamp' | 'syncStatus' | 'syncAttempts' | 'lastSyncAttempt'>
): Promise<SuspectedReport> {
  const fullRecord: SuspectedReport = {
    ...record,
    id: generateUUID(),
    reportNumber: generateReportNumberForSuspected(),
    timestamp: Date.now(),
    syncStatus: 'pending',
    syncAttempts: 0,
    lastSyncAttempt: null
  };

  await db.suspectedReports.add(fullRecord);
  return fullRecord;
}

/**
 * 获取所有可疑上报记录
 */
export async function getAllSuspectedReports(): Promise<SuspectedReport[]> {
  return db.suspectedReports.orderBy('timestamp').reverse().toArray();
}

/**
 * 更新可疑上报同步状态
 */
export async function updateSuspectedSyncStatus(
  id: string,
  status: 'pending' | 'synced' | 'failed',
  incrementAttempts: boolean = false
): Promise<void> {
  const record = await db.suspectedReports.get(id);
  if (record) {
    await db.suspectedReports.update(id, {
      syncStatus: status,
      lastSyncAttempt: Date.now(),
      ...(incrementAttempts && { syncAttempts: record.syncAttempts + 1 })
    });
  }
}

// ==================== 收藏相关 ====================

/**
 * 添加收藏
 */
export async function addFavorite(childId: string): Promise<FavoriteChild> {
  const favorite: FavoriteChild = {
    id: generateUUID(),
    childId,
    addedAt: Date.now()
  };
  await db.favoriteChildren.add(favorite);
  return favorite;
}

/**
 * 移除收藏
 */
export async function removeFavorite(childId: string): Promise<void> {
  await db.favoriteChildren.where('childId').equals(childId).delete();
}

/**
 * 检查是否已收藏
 */
export async function isFavorited(childId: string): Promise<boolean> {
  const count = await db.favoriteChildren.where('childId').equals(childId).count();
  return count > 0;
}

/**
 * 获取所有收藏
 */
export async function getAllFavorites(): Promise<FavoriteChild[]> {
  return db.favoriteChildren.orderBy('addedAt').reverse().toArray();
}

export { db };
